// This file exists because of circular dependency errors
// https://ngrx-entity-relationship.sudo.eu/help/circular-dependency


import { selectFocusedLocation, selectLocations, selectSelectedLocations } from '../../location-list/state/location-list-selectors';
import { selectGoogleMapsApiLoaded } from '../../../../store/global/core-selectors';
import { selectIsMapActive } from '../../state/locations-skeleton-selectors';
import { areCoordsValid, DefaultMapMarker, getCoordinateField, getMarkerUrl, MarkerLocation } from '../map-utils';
import { round } from '@turf/helpers';
import { groupBy } from 'lodash';
import { getAlphabetNumberingSchemeLetter } from '../../../../core/utilities';
import { isCcCandidate } from '../../location-utils';
import { CCLocation } from '../../../../models/locations-models/cc-location';
import { Location } from '../../../../models/locations-models/location';
import { selectSelectedCcLocations } from '../../cc-list/state/cc-list-combined-selectors';
import { selectIsCcOn } from '../../state/location-skeleton-combined-selectors';
import { createSelector } from '@ngrx/store';
import { selectAreMarkersMinimized } from './map-selectors';

/**
 * Used by map UI in CV mode
 */
export const selectCanContinueAfterValidation = createSelector(selectFocusedLocation,
    selectLocations, (focusedLocation, locations) => {
      if (!focusedLocation || !locations.length) {
        return false;
      }
      return focusedLocation.id !== locations[locations.length - 1].id;
    });


/***************************************************************************************************
 * MAP MARKER SELECTORS
 **************************************************************************************************/


export const selectMarkersForSelectedLocations = createSelector(
    selectSelectedLocations,
    selectSelectedCcLocations,
    selectAreMarkersMinimized,
    selectGoogleMapsApiLoaded,
    selectIsMapActive,
    selectIsCcOn,
    (
        selectedLocations,
        selectedCcLocations,
        markersMinimized,
        googleMapsApiLoaded,
        mapActive,
        ccOn
    ) => {
      // don't need markers unless map is active and Maps-scripts are ready

      if (!mapActive || !googleMapsApiLoaded) {
        return [];
      }

      // Use coordinateImport for DEV environment as we don't sync coordinates there.

      const coordinateField = getCoordinateField();

      const getRoundedCoordinates = (markerLocation: MarkerLocation) => {
        return {
          latitude: round(markerLocation.location[coordinateField].latitude, 5),
          longitude: round(markerLocation.location[coordinateField].longitude, 5),
        }
      };

      // create MarkerLocations

      const ungroupedMarkerLocations = selectedLocations
          .filter(selectedLocation => areCoordsValid(selectedLocation[coordinateField]))
          .map((location: Location, i: number) => {
            const markerLocation: MarkerLocation = {
              location,
              url: getMarkerUrl(location),
              label: {
                fontSize: '15px',
                fontFamily: 'Roobert,sans-serif',
                fontWeight: '700',
                text: getAlphabetNumberingSchemeLetter(i),
                color: 'white',
              }
            };
            return markerLocation;
          });

      if (ccOn) {
        selectedCcLocations
            .filter(ccLocation => areCoordsValid(ccLocation[coordinateField]))
            .forEach((ccLocation, i) => {
              const markerLocation: MarkerLocation = {
                location: ccLocation,
                url: getMarkerUrl(ccLocation),
                label: {
                  fontSize: '15px',
                  fontFamily: 'Roobert,sans-serif',
                  fontWeight: '700',
                  text: (i + 1).toString(),
                  color: 'white',
                }
              };
              ungroupedMarkerLocations.push(markerLocation);
            });
      }

      // Create a nested array of MarkerLocation arrays where the inner arrays include
      // MarkerLocation(s) with same geo-coordinates.

      const groupBySameCoordinates: (markerLocations: MarkerLocation[]) =>
          { [latLngString: string]: MarkerLocation[] } = () => {
        return groupBy(ungroupedMarkerLocations, (markerLocation: MarkerLocation) => {
          const roundedCoordinates = getRoundedCoordinates(markerLocation);
          return roundedCoordinates.latitude + ',' +
              roundedCoordinates.longitude;
        });
      };

      const groupedMarkerLocations: MarkerLocation[][] = Object.values(
          groupBySameCoordinates(ungroupedMarkerLocations),
      );
      // Nest individual groups of MarkerLocations into a generated DefaultMapMarker which will then have
      // either the multi-marker SVG (circle with location #) if groups include > 1 MarkerLocation or
      // the respective MarkerLocation SVG if not.
      // Return an array of DefaultMapMarkers.

      return groupedMarkerLocations
          .map((group: MarkerLocation[]) => {
            const locationCount = group.length;
            const multiMarkerSvg = locationCount > 1 ?
                markersMinimized ?
                    'assets/images/mapMarkers/multi_marker_icon_minimized.png' :
                    'assets/images/mapMarkers/multi_marker_icon.png' :
                undefined;
            const roundedCoordinates = getRoundedCoordinates(group[0]);

            const marker: DefaultMapMarker = {
              id: String(roundedCoordinates.latitude) +
                  String(roundedCoordinates.longitude),
              markerLocations: group,
              position: {
                lat: roundedCoordinates.latitude,
                lng: roundedCoordinates.longitude,
              },
              options: {
                // Needs to be here so that when marker size changes, it affects info window position
                // of already opened info windows
                anchorPoint: new google.maps.Point(0, -56), // 56 = regular marker height
                iconData: {
                  default: multiMarkerSvg ? 'assets/images/mapMarkers/multi_marker_icon.png' : group[0].url.default,
                  minimized: multiMarkerSvg ? 'assets/images/mapMarkers/multi_marker_icon_minimized.png' : group[0].url.minimized,
                  label: multiMarkerSvg ? null : group[0].label,
                },
                // will be actually rendered
                icon: {
                  url: multiMarkerSvg ?
                      multiMarkerSvg :
                      markersMinimized ? group[0].url.minimized
                          : group[0].url.default,
                  labelOrigin: new google.maps.Point(40/2, 22) // label position
                },
                label: !multiMarkerSvg && !markersMinimized ? group[0].label : ''
              },
            };
            return marker;
          });
    },
);
/**
 * Get specific marker location
 * @param location
 */
export const selectMarkerLocation = (location: Location | CCLocation) => createSelector(
    selectMarkersForSelectedLocations, (selectedLocationsMarkers) => {
      const identifier = isCcCandidate(location) ? 'placeId' : 'id';

      if (!location?.[identifier]) {
        return;
      }

      for (let m of selectedLocationsMarkers) {
        const possibleMatch = m.markerLocations
            .filter(ml => isCcCandidate(ml.location) === isCcCandidate(location)) // filter same location type
            ?.find(ml => ml.location[identifier] === location[identifier]);

        if (possibleMatch) {
          return possibleMatch;
        }
      }

      return;
    },
);
/**
 * Get DefaultMarker
 * @param markerId
 */
export const selectMarker = (markerId: string) => createSelector(
    selectMarkersForSelectedLocations, (selectedLocationsMarkers) => {
      if (!markerId) {
        return;
      }
      return selectedLocationsMarkers.find(marker => marker.id === markerId);
    },
);